import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '@/shared/cn';
import {
  COMPOSER_CHROME_TRIGGER,
  COMPOSER_CHROME_TRIGGER_LABEL,
  DISABLED_INTERACTIVE,
  MENU_CONTENT,
} from '@/shared/ui-class';
// fork(oem v1.8): the composer offers `CHAT_MODELS` (the local engine only),
// not the provider catalogue `SEED_MODELS` whose choice never reached it.
import {
  CHAT_MODELS as SEED_MODELS,
  COMPOSER_STYLE_LABELS,
  LOCAL_ENGINE_MODEL_ID as DEFAULT_MODEL_ID,
} from '@/versions/v1/shared/chat-types';
import { useChat } from '@/versions/v1/shared/ChatContext';
import { ProviderLogo } from './ProviderLogo';

const MENU_WIDTH_PX = 320;
const MENU_GAP_PX = 8;

export function ModelSelector() {
  const { modelId, setModelId, composerStyle } = useChat();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const active =
    SEED_MODELS.find((m) => m.id === modelId) ??
    SEED_MODELS.find((m) => m.id === DEFAULT_MODEL_ID) ??
    SEED_MODELS[0];

  const styleLabel =
    composerStyle === 'default' ? '' : COMPOSER_STYLE_LABELS[composerStyle];

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const update = () => {
      const rect = triggerRef.current!.getBoundingClientRect();
      setMenuPos({
        top: rect.top - MENU_GAP_PX,
        left: Math.max(8, rect.right - MENU_WIDTH_PX),
      });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (rootRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // fork(oem v1.8): one engine, nothing to choose — a static label, not a
  // dropdown that implies a choice. The sub-label rides the tooltip.
  if (SEED_MODELS.length === 1) {
    const title = `${active.label} — ${active.description}${styleLabel ? ` · Style: ${styleLabel}` : ''}`;
    return (
      <div
        role="note"
        aria-label={`Answered by ${active.label}, ${active.description}`}
        title={title}
        className={cn(COMPOSER_CHROME_TRIGGER, 'cursor-default hover:bg-transparent dark:hover:bg-transparent')}
      >
        <ProviderLogo provider={active.provider} size={14} />
        <span className={cn('truncate text-left', COMPOSER_CHROME_TRIGGER_LABEL)}>
          {active.shortLabel}
        </span>
        {styleLabel ? (
          <span className={cn('truncate text-xs font-normal text-muted', COMPOSER_CHROME_TRIGGER_LABEL)}>
            / {styleLabel}
          </span>
        ) : null}
      </div>
    );
  }

  return (
    <div className="relative" ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        aria-label={
          styleLabel
            ? `Model: ${active.shortLabel}, style: ${styleLabel}`
            : `Model: ${active.shortLabel}`
        }
        title={
          styleLabel
            ? `Model: ${active.shortLabel} · Style: ${styleLabel}`
            : `Model: ${active.shortLabel}`
        }
        onClick={() => setOpen((o) => !o)}
        className={cn(
          COMPOSER_CHROME_TRIGGER,
          open && 'bg-zinc-100 dark:bg-zinc-800/60',
          DISABLED_INTERACTIVE,
        )}
      >
        <ProviderLogo provider={active.provider} size={14} />
        <span className={cn('truncate text-left', COMPOSER_CHROME_TRIGGER_LABEL)}>
          {active.shortLabel}
        </span>
        {styleLabel ? (
          <>
            <span
              aria-hidden
              className={cn(
                'select-none text-xs font-light leading-none text-zinc-300 dark:text-zinc-600',
                COMPOSER_CHROME_TRIGGER_LABEL,
              )}
            >
              /
            </span>
            <span
              className={cn(
                'truncate text-xs font-normal text-muted',
                COMPOSER_CHROME_TRIGGER_LABEL,
              )}
            >
              {styleLabel}
            </span>
          </>
        ) : null}
        <ChevronDown
          className={cn(
            'h-3.5 w-3.5 flex-shrink-0 text-zinc-400 dark:text-zinc-500',
            COMPOSER_CHROME_TRIGGER_LABEL,
          )}
        />
      </button>
      {open && typeof document !== 'undefined'
        ? createPortal(
            <div
              ref={menuRef}
              className={cn(
                MENU_CONTENT,
                'fixed z-[200] w-[320px] overflow-hidden',
              )}
              style={{
                top: menuPos.top,
                left: menuPos.left,
                transform: 'translateY(-100%)',
              }}
            >
              {SEED_MODELS.map((model) => {
                const isActive = model.id === modelId;
                return (
                  <button
                    key={model.id}
                    type="button"
                    className="group flex w-full cursor-pointer items-start gap-2.5 rounded-sm px-2 py-2 text-left text-sm outline-none hover:bg-zinc-100 dark:hover:bg-zinc-900"
                    onClick={() => {
                      setModelId(model.id);
                      setOpen(false);
                    }}
                  >
                    <div className="mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center">
                      <ProviderLogo provider={model.provider} size={14} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium tracking-tight text-zinc-900 dark:text-zinc-100">
                        {model.label}
                      </div>
                      <div className="text-xs text-muted">{model.description}</div>
                    </div>
                    <div className="mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center">
                      {isActive ? (
                        <Check className="h-4 w-4 text-zinc-900 dark:text-zinc-100" />
                      ) : null}
                    </div>
                  </button>
                );
              })}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
